import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { FaEnvelope, FaGraduationCap, FaLock, FaMapMarkerAlt, FaPhone, FaUser, FaUserCog, FaUserMd, FaUserPlus } from 'react-icons/fa';
import RankMultiSelect from './RankMultiSelect';
import RankSelect from './RankSelect';
import { getApiUrl } from '../utils/config';
import { formatApiError } from '../utils/apiErrorFormatter';
import { useToast } from '../utils/toastContext';
import '../styles/theme.css';
import '../styles/CreateUserFormModern.css';

const emptyForm = {
  email: '',
  password: '',
  firstname: '',
  lastname: '',
  phone: '',
  address: '',
  nationality: '',
  specialityIds: [],   // multi-select; min 1 for preceptors
  institutionIds: [],  // multi-select; min 1 for preceptors
  subSpeciality: '',
  subInstitution: '',
  isAdmin: false,
  rank: '',       // students: single rank name
  rankIds: []     // preceptors: multi-select rank ids
};

const normalizeLookup = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    displayName: item.name || item.speciality || item.title || item._id
  }));

const CreateUserFormModern = () => {
  const { showSuccess, showError } = useToast();
  const [userType, setUserType] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [subSpecialities, setSubSpecialities] = useState([]);
  const [subInstitutions, setSubInstitutions] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);

  useEffect(() => {
    const loadLookups = async () => {
      setLookupLoading(true);
      try {
        const authHeaders = {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        };

        const [specialtiesResponse, institutionsResponse] = await Promise.all([
          axios.get(getApiUrl('crud/get_all_specialities'), { headers: authHeaders }),
          axios.get(getApiUrl('crud/get_all_institutions'), { headers: authHeaders })
        ]);

        setSpecialties(normalizeLookup(specialtiesResponse.data));
        setInstitutions(normalizeLookup(institutionsResponse.data));
      } catch (err) {
        showError(formatApiError(err, 'Failed to load user form data.'));
      } finally {
        setLookupLoading(false);
      }
    };

    loadLookups();
  }, [showError]);

  const specialtyOptions = useMemo(() => specialties, [specialties]);
  const institutionOptions = useMemo(() => institutions, [institutions]);

  const updateSubSpecialities = (specialityIds) => {
    // Show sub-specialities for the first selected specialty only
    const firstId = Array.isArray(specialityIds) ? specialityIds[0] : specialityIds;
    const speciality = specialties.find((item) => item._id === firstId);
    setSubSpecialities(Array.isArray(speciality?.sub_specialities) ? speciality.sub_specialities : Array.isArray(speciality?.subSpecialities) ? speciality.subSpecialities : []);
  };

  const updateSubInstitutions = (institutionIds) => {
    // Show sub-institutions for the first selected institution only
    const firstId = Array.isArray(institutionIds) ? institutionIds[0] : institutionIds;
    const institution = institutions.find((item) => item._id === firstId);
    setSubInstitutions(Array.isArray(institution?.sub_institutions) ? institution.sub_institutions : Array.isArray(institution?.subInstitutions) ? institution.subInstitutions : []);
  };

  const handleSpecialityChange = (selected) => {
    const ids = selected ? selected.map((opt) => opt.value) : [];
    setFormData((prev) => ({
      ...prev,
      specialityIds: ids,
      subSpeciality: ''
    }));
    updateSubSpecialities(ids);
  };

  const handleInstitutionChange = (selected) => {
    const ids = selected ? selected.map((opt) => opt.value) : [];
    setFormData((prev) => ({
      ...prev,
      institutionIds: ids,
      subInstitution: ''
    }));
    updateSubInstitutions(ids);
  };

  const setField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const clearForm = () => {
    setFormData(emptyForm);
    setUserType('');
    setSubSpecialities([]);
    setSubInstitutions([]);
  };

  const buildPayload = () => {
    if (!userType) {
      throw new Error('Please select a user type.');
    }
    if (!formData.firstname.trim()) {
      throw new Error('First name is required.');
    }
    if (!formData.lastname.trim()) {
      throw new Error('Last name is required.');
    }
    if (!formData.email.trim()) {
      throw new Error('Email is required.');
    }
    if (!formData.password.trim()) {
      throw new Error('Password is required.');
    }
    if (!formData.phone.trim()) {
      throw new Error('Phone is required.');
    }
    if (!formData.address.trim()) {
      throw new Error('Address is required.');
    }

    const payload = new FormData();
    payload.append('firstname', formData.firstname);
    payload.append('lastname', formData.lastname);
    payload.append('email', formData.email);
    payload.append('password', formData.password);
    payload.append('phone', formData.phone);
    payload.append('address', formData.address);
    if (formData.nationality) payload.append('nationality', formData.nationality);

    if (userType === 'student') {
      if (!formData.rank) {
        throw new Error('Rank is required for students.');
      }
      payload.append('rank', formData.rank);
      return { endpoint: getApiUrl('crud/insert_student/'), payload };
    }

    if (userType === 'preceptor') {
      if (!formData.specialityIds || formData.specialityIds.length === 0) {
        throw new Error('At least one Rotation/Experience is required for preceptors.');
      }
      if (!formData.institutionIds || formData.institutionIds.length === 0) {
        throw new Error('At least one Institution is required for preceptors.');
      }
      if (!formData.rankIds || formData.rankIds.length === 0) {
        throw new Error('At least one rank is required for preceptors.');
      }

      // Send each list as separate form fields so FastAPI collects them as List[str]
      formData.specialityIds.forEach((id) => payload.append('specialityIds', id));
      formData.rankIds.forEach((id) => payload.append('rankIds', id));
      formData.institutionIds.forEach((id) => payload.append('institutionIds', id));
      payload.append('isAdmin', String(formData.isAdmin));
      if (formData.subSpeciality) payload.append('subSpeciality', formData.subSpeciality);
      if (formData.subInstitution) payload.append('subInstitution', formData.subInstitution);
      return { endpoint: getApiUrl('crud/add_preceptor/'), payload };
    }

    if (userType === 'admin') {
      return { endpoint: getApiUrl('crud/add_admin/'), payload };
    }

    throw new Error('Invalid user type');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const { endpoint, payload } = buildPayload();
      await axios.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      showSuccess(`${userType || 'User'} created successfully.`);
    } catch (err) {
      showError(formatApiError(err, 'Failed to create user. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cuf-page">
      <section className="cuf-shell">
        <section className="cuf-form-panel">
          <header className="cuf-header">
            <div className="cuf-header-main">
              <div className="cuf-header-icon"><FaUserPlus /></div>
              <div>
                <h2>Create User</h2>
                <p>Build student, preceptor, and administrator accounts in a clean, focused workflow.</p>
              </div>
            </div>
          </header>

          <form className="cuf-form" onSubmit={handleSubmit}>
            <div className="cuf-section">
              <div className="cuf-section-title">
                <FaUser />
                <h3>User type</h3>
              </div>
              <div className="cuf-type-grid">
                {[
                  { value: 'student', label: 'Student', icon: <FaGraduationCap /> },
                  { value: 'preceptor', label: 'Preceptor', icon: <FaUserMd /> },
                  { value: 'admin', label: 'Admin', icon: <FaUserCog /> }
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`cuf-type-card ${userType === option.value ? 'is-active' : ''}`}
                    onClick={() => setUserType(option.value)}
                  >
                    <span className="cuf-type-card__icon">{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="cuf-section">
              <div className="cuf-section-title">
                <FaUser />
                <h3>Basic information</h3>
              </div>
              <div className="cuf-grid">
                <label className="cuf-field">
                  <span>First name</span>
                  <input type="text" value={formData.firstname} onChange={setField('firstname')} placeholder="First name" required />
                </label>
                <label className="cuf-field">
                  <span>Last name</span>
                  <input type="text" value={formData.lastname} onChange={setField('lastname')} placeholder="Last name" required />
                </label>
                <label className="cuf-field cuf-field--full">
                  <span>Full name</span>
                  <input type="text" value={`${formData.firstname} ${formData.lastname}`.trim()} readOnly disabled placeholder="First name + Last name" />
                </label>
                <label className="cuf-field cuf-field--full">
                  <span>Email</span>
                  <div className="cuf-input-wrap">
                    <FaEnvelope />
                    <input type="email" value={formData.email} onChange={setField('email')} placeholder="Email address" required />
                  </div>
                </label>
                <label className="cuf-field cuf-field--full">
                  <span>Password</span>
                  <div className="cuf-input-wrap">
                    <FaLock />
                    <input type="password" value={formData.password} onChange={setField('password')} placeholder="Password" required />
                  </div>
                </label>
                <label className="cuf-field">
                  <span>Phone</span>
                  <div className="cuf-input-wrap">
                    <FaPhone />
                    <input type="tel" value={formData.phone} onChange={setField('phone')} placeholder="Phone number" required />
                  </div>
                </label>
                <label className="cuf-field">
                  <span>Address</span>
                  <div className="cuf-input-wrap">
                    <FaMapMarkerAlt />
                    <input type="text" value={formData.address} onChange={setField('address')} placeholder="Address" required />
                  </div>
                </label>
                <label className="cuf-field">
                  <span>Nationality</span>
                  <input type="text" value={formData.nationality} onChange={setField('nationality')} placeholder="Nationality" />
                </label>
              </div>
            </div>

            {userType === 'student' ? (
              <div className="cuf-section">
                <div className="cuf-section-title">
                  <FaGraduationCap />
                  <h3>Academic information</h3>
                </div>
                <label className="cuf-field cuf-field--full">
                  <span>Rank</span>
                  <RankSelect value={formData.rank} onChange={setField('rank')} required />
                </label>
              </div>
            ) : null}

            {userType === 'preceptor' ? (
              <div className="cuf-section">
                <div className="cuf-section-title">
                  <FaUserMd />
                  <h3>Professional information</h3>
                </div>
                <div className="cuf-grid">
                  <label className="cuf-field cuf-field--full">
                    <span>Rotation / experience <span style={{ color: '#ef4444' }}>*</span> <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.85em' }}>(select one or more)</span></span>
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      isDisabled={lookupLoading}
                      value={specialtyOptions
                        .filter((s) => formData.specialityIds.includes(s._id))
                        .map((s) => ({ value: s._id, label: s.displayName }))}
                      onChange={handleSpecialityChange}
                      options={specialtyOptions.map((s) => ({ value: s._id, label: s.displayName }))}
                      placeholder={lookupLoading ? 'Loading...' : 'Select rotation(s) / experience(s)'}
                      classNamePrefix="cuf-spec-select"
                      styles={{
                        container: (b) => ({ ...b, width: '100%' }),
                        control: (b) => ({ ...b, minHeight: 44, fontSize: '0.95rem' }),
                        placeholder: (b) => ({ ...b, color: '#94a3b8' }),
                      }}
                    />
                  </label>
                  <label className="cuf-field cuf-field--full">
                    <span>Institution <span style={{ color: '#ef4444' }}>*</span> <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.85em' }}>(select one or more)</span></span>
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      isDisabled={lookupLoading}
                      value={institutionOptions
                        .filter((i) => formData.institutionIds.includes(i._id))
                        .map((i) => ({ value: i._id, label: i.displayName }))}
                      onChange={handleInstitutionChange}
                      options={institutionOptions.map((i) => ({ value: i._id, label: i.displayName }))}
                      placeholder={lookupLoading ? 'Loading...' : 'Select institution(s)'}
                      classNamePrefix="cuf-inst-select"
                      styles={{
                        container: (b) => ({ ...b, width: '100%' }),
                        control: (b) => ({ ...b, minHeight: 44, fontSize: '0.95rem' }),
                        placeholder: (b) => ({ ...b, color: '#94a3b8' }),
                      }}
                    />
                  </label>
                  <label className="cuf-field">
                    <span>Sub-rotation / experience</span>
                    <select value={formData.subSpeciality} onChange={setField('subSpeciality')}>
                      <option value="">None</option>
                      {subSpecialities.map((item) => (
                        <option key={item} value={item}>{item}</option>
                      ))}
                    </select>
                  </label>
                  <label className="cuf-field">
                    <span>Sub-institution</span>
                    <select value={formData.subInstitution} onChange={setField('subInstitution')}>
                      <option value="">None</option>
                      {subInstitutions.map((item) => (
                        <option key={item} value={item}>{item}</option>
                      ))}
                    </select>
                  </label>
                  <label className="cuf-field">
                    <span>Administrator access</span>
                    <button
                      type="button"
                      className={`cuf-toggle ${formData.isAdmin ? 'is-on' : ''}`}
                      onClick={() => setFormData((prev) => ({ ...prev, isAdmin: !prev.isAdmin }))}
                    >
                      {formData.isAdmin ? 'Enabled' : 'Disabled'}
                    </button>
                  </label>
                  <label className="cuf-field cuf-field--full">
                    <span>Rank(s) <span style={{ color: '#ef4444' }}>*</span> <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.85em' }}>(select one or more)</span></span>
                    <RankMultiSelect
                      value={formData.rankIds}
                      onChange={(rankIds) => setFormData((prev) => ({ ...prev, rankIds }))}
                      required
                    />
                  </label>
                </div>
              </div>
            ) : null}

            <div className="cuf-actions">
              <button type="submit" className="cuf-submit" disabled={loading || !userType}>
                {loading ? 'Creating user...' : 'Create user'}
              </button>
            </div>
          </form>
        </section>
      </section>
    </div>
  );
};

export default CreateUserFormModern;
